<template>
  <div class="kapplan-tri" ref="rootEl">
    <!-- Header + Wochen-Navigation -->
    <div class="kp-header">
      <h1>Kapazitätsplanung</h1>
      <div class="nav-stack">
        <div class="week-nav">
          <button class="week-btn" @click="shiftWeek(-1)" title="Vorige Woche">
            <i class="fas fa-angles-left"></i> Woche
          </button>
          <button class="week-btn week-now" @click="goToday" title="Aktuelle Woche">
            {{ weekLabel }}
          </button>
          <button class="week-btn" @click="shiftWeek(1)" title="Nächste Woche">
            Woche <i class="fas fa-angles-right"></i>
          </button>
          <button class="week-btn refresh-btn" @click="loadOrders" :disabled="loading" title="Neu laden">
            <i class="fas fa-rotate" :class="{ spinning: loading }"></i>
          </button>
        </div>
        <!-- Tage-Verschiebung (Chef-Wunsch 10.06.): Fenster tageweise schieben. -->
        <div class="day-nav">
          <button class="day-btn" @click="shiftDay(-1)" title="Einen Tag zurück">
            <i class="fas fa-chevron-left"></i> Tag
          </button>
          <span class="day-nav-label">tageweise verschieben</span>
          <button class="day-btn" @click="shiftDay(1)" title="Einen Tag vor">
            Tag <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Live-Diagnose: zeigt sofort, wo Daten verloren gehen -->
    <div class="kp-diag" :class="{ 'kp-diag-empty': orders.length === 0 }">
      <span class="kp-diag-pill">
        <i class="fas fa-database"></i> {{ orders.length }} geladen
      </span>
      <span class="kp-diag-pill">
        <i class="fas fa-print"></i> {{ druckMatchedCount }} auf Druck-Maschine
      </span>
      <span class="kp-diag-pill">
        <i class="fas fa-cogs"></i> {{ wvMatchedCount }} auf WV-Maschine
      </span>
      <span class="kp-diag-pill">
        <i class="fas fa-calendar-week"></i> {{ druckOrdersCount + wvOrdersCount }} in dieser Woche
      </span>
      <span class="kp-diag-pill kp-diag-shift" :title="splitByShift ? 'Zwei Schichten je Tag: 06–14 und 14–24 (16 h Kapazität je Maschine)' : 'Eine Schicht je Tag (8 h Kapazität je Maschine)'">
        <i class="fas fa-user-clock"></i> {{ splitByShift ? '2-Schicht-Betrieb' : '1-Schicht-Betrieb' }}
      </span>
      <span v-if="unmappedMachines.length" class="kp-diag-pill kp-diag-warn" :title="unmappedMachines.join(', ')">
        <i class="fas fa-exclamation-triangle"></i>
        {{ unmappedMachines.length }} unbekannte Maschine(n)
      </span>
    </div>

    <!-- Tri-View Layout: Druck oben, WV + Überfällige unten -->
    <div class="kp-layout" :style="layoutCssVars">

      <!-- ───── A) Druck-Maschinen Zeitstrahl (oben, voll breit) ───── -->
      <section class="kp-panel kp-druck" :class="{ 'is-resizing': resize === 'h' }">
        <header class="panel-head">
          <i class="fas fa-print"></i>
          <span>Druck-Maschinen</span>
          <small class="panel-info">{{ druckOrdersCount }} Auftrag(s) in dieser Woche</small>
        </header>

        <div class="timeline">
          <!-- Day header -->
          <div class="row row-head">
            <div class="cell-machine"></div>
            <div
              v-for="(d, idx) in workDays"
              :key="`hd-${d.iso}`"
              class="cell-day"
              :class="{ today: d.isToday, yesterday: d.isYesterday, weekend: d.isWeekend, past: d.isPast, empty: !d.hasJobs, narrow: d.isNarrow }"
              :style="{ flexGrow: d.flexGrow }"
            >
              <div class="day-label">{{ d.isNarrow ? d.dateLabelShort : d.dateLabel }}</div>
            </div>
          </div>

          <!-- One row per druck machine + der graue Summenbalken darunter -->
          <template v-for="m in druckMachines" :key="`drw-${m.code}`">
          <div
            class="row row-machine"
            :class="{ 'row-drop-hover': dragKind === 'druck' && dragOverCode === m.code }"
          >
            <div class="cell-machine">{{ m.display }}</div>
            <div
              v-for="d in workDays"
              :key="`dr-${m.code}-${d.iso}`"
              class="cell-day cell-slot"
              :class="{ today: d.isToday, yesterday: d.isYesterday, weekend: d.isWeekend, past: d.isPast, empty: !d.hasJobs, narrow: d.isNarrow, 'col-drop-hover': dragKind === 'druck' && dragOverIso === d.iso, 'drop-target': dragOverKey === `druck|${m.code}|${d.iso}` }"
              :style="{ flexGrow: d.flexGrow }"
              @dragover.prevent="onDragOver('druck', m.code, d.iso)"
              @dragleave="onDragLeave"
              @drop.prevent="onDrop('druck', m.code, d.iso)"
            >
              <!-- Schicht-Gruppen (#16 / Boss „2-Schicht-Toggle“): im 2-Schicht-
                   Betrieb wird die Tageszelle sichtbar in Früh- und Spätschicht
                   geteilt, damit Drucker und Chef auf einen Blick sehen, WAS in
                   IHRER Schicht läuft. Im 1-Schicht-Betrieb bleibt genau eine
                   Gruppe ohne Kopfzeile — die Zelle sieht aus wie vorher. -->
              <div
                v-for="g in shiftGroups(m.code, d.iso, 'druck')"
                :key="`sg-dr-${m.code}-${d.iso}-${g.key}`"
                class="shift-group"
                :class="[`load-${g.level}`, { 'is-unassigned': g.shift === null, 'is-overcount': g.overCount }]"
              >
                <div class="shift-group-head" :title="g.title">
                  <span class="sg-name">{{ g.kurz }}</span>
                  <span class="sg-time">{{ g.zeit }}</span>
                  <span class="sg-load"><strong>{{ fmtHM(g.hours) }}</strong><small>/{{ g.capacity }}h</small></span>
                  <span class="sg-count" :class="{ over: g.overCount }" :title="`${g.jobs.length} von ${maxJobsPerShift} Aufträgen in dieser Schicht`">
                    {{ g.jobs.length }}/{{ maxJobsPerShift }}
                  </span>
                  <span v-if="g.produced" class="sg-produced" :title="`In dieser Schicht gemeldet: ${fmtQty(g.produced)} Stück`">
                    <i class="fas fa-layer-group"></i>{{ fmtQty(g.produced) }}
                  </span>
                  <span class="sg-bar"><span class="sg-bar-fill" :style="{ width: Math.min(100, g.pct) + '%' }"></span></span>
                </div>
              <div
                v-for="o in g.jobs"
                :key="`j-${o.id}`"
                class="job-card job-card-druck"
                :class="[statusClass(o.status), { started: isStarted(o), forgotten: isForgotten(o, d.iso), ready: isReady(o), 'planning-draft': isDraft(o), movable: canMove, 'is-dragging': dragOrder && dragOrder.id === o.id }]"
                :style="druckCardStyle(o)"
                :draggable="canMove"
                @dragstart="onDragStart(o, 'druck', m.code, $event)"
                @dragend="onDragEnd"
                @click="openOrder(o.id)"
                :title="`${o.order_number} · ${o.customer_name} · ${o.description || ''}${o.employee_name ? ' · ' + o.employee_name : ''}`"
              >
                <!-- Seitlicher 8-h-Balken (Besprechung 08.06.2026): volle Höhe = 1
                     Schicht (8 h), in 8 Felder geteilt; die Füllung von unten zeigt
                     die kalkulierte Druckzeit als Anteil daran, in der Status-Farbe. -->
                <div class="shift-bar" :title="calcBarTitle(o) || 'ohne kalkulierte Druckzeit'">
                  <span class="shift-bar-fill" :style="{ height: calcBarPct(o) + '%' }"></span>
                </div>
                <!-- Status 3 (Druck läuft): hellgrüner Hintergrund wächst mit der
                     verbrauchten Zeit bis zur kalkulierten Zeit (= voll). -->
                <div
                  v-if="printRunning(o)"
                  class="print-progress"
                  :class="{ over: isOvertime(o) }"
                  :style="{ width: printProgressPct(o) + '%' }"
                ></div>
                <div v-if="canMove" class="move-ctrls">
                  <button class="move-btn" title="Einen Tag zurück" @click.stop="nudgeDay(o, 'druck', -1, m.code)"><i class="fas fa-chevron-left"></i></button>
                  <button class="move-btn" title="Einen Tag vor" @click.stop="nudgeDay(o, 'druck', 1, m.code)"><i class="fas fa-chevron-right"></i></button>
                </div>
                <div class="job-line-1">
                  <strong>{{ o.order_number }}</strong>
                  <order-flags :order="o" />
                </div>
                <div class="job-line-2">{{ truncate(o.customer_name, 18) }}</div>
                <div v-if="o.description" class="job-desc" :title="o.description">{{ truncate(o.description, 40) }}</div>
                <div v-if="o.delivery_date" class="job-due" :title="'Anlieferungs-Termin beim Kunden'">
                  <i class="fas fa-truck"></i> {{ shortDate(o.delivery_date) }}
                </div>
                <div class="job-line-3">
                  <farbigkeit-square :value="o.farbigkeit || o.color" />
                  <span class="job-run" title="Druckanzahl">{{ formatRun(o.print_run) }}</span>
                  <span v-if="o.druckform_count" class="df-badge" :title="'Anzahl Druckformen / Durchgänge: ' + o.druckform_count">
                    <i class="fas fa-stamp"></i>{{ o.druckform_count }}
                  </span>
                  <span v-for="c in (o.druckart_codes || [])" :key="c" class="da-badge">{{ c }}</span>
                  <span
                    v-if="overflowHours(o)"
                    class="of-badge"
                    :title="`Überläufer: ${fmtHM(calcHours(o))} passen nicht mehr in diesen Tag — ${fmtHM(overflowHours(o))} laufen auf den Folgetag`"
                  ><i class="fas fa-angles-right"></i>+{{ Math.ceil(overflowHours(o)) }}h</span>
                  <span
                    v-if="o.employee_id || o.employee_name"
                    class="job-emp"
                    :title="o.employee_name || ''"
                  >
                    <user-avatar
                      class="job-emp-avatar"
                      :src="o.employee_icon_url"
                      :seed="o.employee_id"
                      :name="o.employee_name || '?'"
                      :size="22"
                    />
                    <span class="job-initials">{{ initials(o.employee_name) }}</span>
                  </span>
                </div>
                <!-- Status 3: kalkulierte vs. verbrauchte Zeit; Überzeit halbfett rot. -->
                <div v-if="printRunning(o)" class="print-time" :class="{ over: isOvertime(o), 'over-120': isOver120(o) }" :title="printTimeTitle(o)">
                  <i class="fas fa-stopwatch"></i>
                  <span>{{ fmtHM(consumedHours(o)) }} / {{ fmtHM(calcHours(o)) }}</span>
                  <strong v-if="isOvertime(o)" class="print-over-x">+{{ fmtHM(overtimeHours(o)) }}</strong>
                </div>
                <div v-if="producedQty(o)" class="job-produced" :title="'Exemplare fertig: ' + qtyLabel(o) + ' Stück'">
                  <i class="fas fa-layer-group"></i> {{ qtyLabel(o) }}
                </div>
              </div>
              </div>
              <!-- Überläufer vom Vortag (08.06.2026.md): Höhe = Reststunden,
                   mindestens 1 h. Nicht verschiebbar — die Merkung wird aus der
                   kalkulierten Zeit abgeleitet und verschwindet von selbst,
                   sobald der Auftrag an der Maschine fertig gemeldet ist. -->
              <div
                v-for="g in ghostsAt(m.code, d.iso)"
                :key="`gh-dr-${g.order.id}-${d.iso}`"
                class="job-card job-ghost"
                :class="statusClass(g.order.status)"
                :style="ghostStyle(g)"
                :title="ghostTitle(g)"
                @click="openOrder(g.order.id)"
              >
                <div class="ghost-head">
                  <i class="fas fa-angles-right"></i>
                  <strong>{{ g.order.order_number }}</strong>
                  <span class="ghost-rest">{{ fmtHM(g.hours) }}</span>
                </div>
                <div class="ghost-sub">Überläufer vom {{ shortDate(g.fromIso) }}</div>
              </div>

              <!-- Tages-Auslastung der Maschine: die Zahl, die der Chef sucht.
                   Belegte Stunden gegen die Tageskapazität (8 h bzw. 16 h). -->
              <div
                v-if="cellHasContent(m.code, d.iso)"
                class="day-load"
                :class="`load-${dayLoad(m.code, d.iso).level}`"
                :title="dayLoadTitle(m.code, d.iso)"
              >
                <span class="dl-bar"><span class="dl-bar-fill" :style="{ width: Math.min(100, dayLoad(m.code, d.iso).pct) + '%' }"></span></span>
                <span class="dl-text">
                  <strong>{{ fmtHM(dayLoad(m.code, d.iso).hours) }}</strong>/{{ dayLoad(m.code, d.iso).capacity }}h
                  <em>{{ dayLoad(m.code, d.iso).pct }}%</em>
                </span>
              </div>
              <div v-if="!cellHasContent(m.code, d.iso)" class="cell-empty"></div>
            </div>
          </div>

          <!-- Tageweise Summierung (08.06.2026 / 16.06.2026 #8): grauer
               Trennbalken je Maschine, pro Tagesspalte die Summen der an
               dieser Maschine FERTIG gemeldeten Jobs — Aufträge, Formen,
               Druckanzahl. Abgelaufene und aktuelle Tage gleichermaßen. -->
          <div class="row row-sum">
            <div class="cell-machine cell-sum-label" :title="`Tagessummen ${m.display}: fertige Aufträge, Formen, Druckanzahl`">
              <i class="fas fa-calculator"></i> Summe
            </div>
            <div
              v-for="d in workDays"
              :key="`drsum-${m.code}-${d.iso}`"
              class="cell-day cell-sum"
              :class="{ today: d.isToday, weekend: d.isWeekend, past: d.isPast, narrow: d.isNarrow, filled: !!dailyTotal(m.code, d.iso) }"
              :style="{ flexGrow: d.flexGrow }"
              :title="dailyTotalTitle(m.code, d.iso)"
            >
              <template v-if="dailyTotal(m.code, d.iso)">
                <span class="sum-part sum-jobs"><i class="fas fa-check"></i>{{ dailyTotal(m.code, d.iso).fertige_auftraege }}</span>
                <span class="sum-part sum-forms"><i class="fas fa-stamp"></i>{{ dailyTotal(m.code, d.iso).formen }}</span>
                <span class="sum-part sum-run">{{ fmtQty(dailyTotal(m.code, d.iso).druckanzahl) }}</span>
              </template>
              <span v-else class="sum-none">–</span>
            </div>
          </div>
          </template>

          <!-- #4 Überfällige Druck-Aufträge: bleiben unten in grau sichtbar
               (vor dem Fenster terminiert, noch nicht ausgeliefert) -->
          <div v-if="overdueByKind.druck.length" class="row row-overdue">
            <div class="cell-machine cell-overdue">
              <i class="fas fa-clock-rotate-left"></i> Überfällig
            </div>
            <div class="cell-day cell-overdue-list">
              <div
                v-for="o in overdueByKind.druck"
                :key="`od-dr-${o.id}`"
                class="job-card overdue-strip"
                :class="statusClass(o.status)"
                @click="openOrder(o.id)"
                :title="`${o.order_number} · ${o.customer_name} · überfällig seit ${overdueWhen(o, 'druck')}`"
              >
                <div class="job-line-1">
                  <strong>{{ o.order_number }}</strong>
                  <order-flags :order="o" />
                </div>
                <div class="job-line-2">{{ truncate(o.customer_name, 16) }}</div>
                <div class="overdue-when"><i class="fas fa-calendar-xmark"></i> {{ overdueWhen(o, 'druck') }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Horizontal resize handle between Druck (top) and Bottom row -->
      <div
        class="resize-handle resize-h"
        :class="{ active: resize === 'h' }"
        title="Höhe der Druck-Ebene ziehen"
        @mousedown.prevent="beginResize('h', $event)"
      ></div>

      <!-- ───── B + C) Untere Reihe: WV-Maschinen | Überfällige ───── -->
      <div class="kp-bottom">

        <!-- B) WV-Maschinen Plan (links) -->
        <section class="kp-panel kp-wv" :class="{ 'is-resizing': resize === 'v' }">
          <header class="panel-head">
            <i class="fas fa-cogs"></i>
            <span>WV-Maschinen</span>
            <small class="panel-info">{{ wvOrdersCount }} in dieser Woche</small>
          </header>

          <div class="timeline timeline-wv">
            <div class="row row-head">
              <div class="cell-machine"></div>
              <div
                v-for="d in workDays"
                :key="`wvh-${d.iso}`"
                class="cell-day"
                :class="{ today: d.isToday, yesterday: d.isYesterday, weekend: d.isWeekend, past: d.isPast, empty: !d.hasJobs, narrow: d.isNarrow }"
                :style="{ flexGrow: d.flexGrow }"
              >
                <div class="day-label">{{ d.isNarrow ? d.dateLabelShort : d.dateLabel }}</div>
              </div>
            </div>

            <template v-for="m in wvMachines" :key="`wvw-${m.code}`">
            <div
              class="row row-machine"
              :class="{ 'row-drop-hover': dragKind === 'wv' && dragOverCode === m.code }"
            >
              <div class="cell-machine">{{ m.display }}</div>
              <div
                v-for="d in workDays"
                :key="`wv-${m.code}-${d.iso}`"
                class="cell-day cell-slot"
                :class="{ today: d.isToday, yesterday: d.isYesterday, weekend: d.isWeekend, past: d.isPast, empty: !d.hasJobs, narrow: d.isNarrow, 'col-drop-hover': dragKind === 'wv' && dragOverIso === d.iso, 'drop-target': dragOverKey === `wv|${m.code}|${d.iso}` }"
                :style="{ flexGrow: d.flexGrow }"
                @dragover.prevent="onDragOver('wv', m.code, d.iso)"
                @dragleave="onDragLeave"
                @drop.prevent="onDrop('wv', m.code, d.iso)"
              >
                <div
                  v-for="g in shiftGroups(m.code, d.iso, 'wv')"
                  :key="`sg-wv-${m.code}-${d.iso}-${g.key}`"
                  class="shift-group"
                  :class="[`load-${g.level}`, { 'is-unassigned': g.shift === null, 'is-overcount': g.overCount }]"
                >
                  <div class="shift-group-head" :title="g.title">
                    <span class="sg-name">{{ g.kurz }}</span>
                    <span class="sg-time">{{ g.zeit }}</span>
                    <span class="sg-load"><strong>{{ fmtHM(g.hours) }}</strong><small>/{{ g.capacity }}h</small></span>
                    <span class="sg-count" :class="{ over: g.overCount }" :title="`${g.jobs.length} von ${maxJobsPerShift} Aufträgen in dieser Schicht`">
                      {{ g.jobs.length }}/{{ maxJobsPerShift }}
                    </span>
                    <span v-if="g.produced" class="sg-produced" :title="`In dieser Schicht gemeldet: ${fmtQty(g.produced)} Stück`">
                      <i class="fas fa-layer-group"></i>{{ fmtQty(g.produced) }}
                    </span>
                    <span class="sg-bar"><span class="sg-bar-fill" :style="{ width: Math.min(100, g.pct) + '%' }"></span></span>
                  </div>
                <div
                  v-for="o in g.jobs"
                  :key="`wvj-${o.id}`"
                  class="job-card job-card-wv"
                  :class="[statusClass(o.status), { started: isStarted(o), forgotten: isForgotten(o, d.iso), ready: isReady(o), 'planning-draft': isDraft(o), movable: canMove, 'is-dragging': dragOrder && dragOrder.id === o.id }]"
                  :draggable="canMove"
                  @dragstart="onDragStart(o, 'wv', m.code, $event)"
                  @dragend="onDragEnd"
                  @click="openOrder(o.id)"
                  :title="`${o.order_number} · ${o.customer_name}${o.employee_name ? ' · ' + o.employee_name : ''}`"
                >
                  <div v-if="canMove" class="move-ctrls">
                    <button class="move-btn" title="Einen Tag zurück" @click.stop="nudgeDay(o, 'wv', -1, m.code)"><i class="fas fa-chevron-left"></i></button>
                    <button class="move-btn" title="Einen Tag vor" @click.stop="nudgeDay(o, 'wv', 1, m.code)"><i class="fas fa-chevron-right"></i></button>
                  </div>
                  <div class="job-line-1">
                    <strong>{{ o.order_number }}</strong>
                    <order-flags :order="o" />
                    <span v-for="c in (o.druckart_codes || [])" :key="c" class="da-badge">{{ c }}</span>
                    <span
                      v-if="o.employee_id || o.employee_name"
                      class="job-emp"
                      :title="o.employee_name || ''"
                    >
                      <user-avatar
                        class="job-emp-avatar"
                        :src="o.employee_icon_url"
                        :seed="o.employee_id"
                        :name="o.employee_name || '?'"
                        :size="20"
                      />
                      <span class="job-initials">{{ initials(o.employee_name) }}</span>
                    </span>
                  </div>
                  <div v-if="calcBarPct(o)" class="calc-bar" :title="calcBarTitle(o)">
                    <span class="calc-bar-fill" :style="{ width: calcBarPct(o) + '%' }"></span>
                  </div>
                  <div v-if="producedQty(o)" class="job-produced" :title="'Produziert: ' + qtyLabel(o) + ' Stück'">
                    <i class="fas fa-layer-group"></i> {{ qtyLabel(o) }}
                  </div>
                </div>
                </div>
                <div
                  v-for="g in ghostsAt(m.code, d.iso, 'wv')"
                  :key="`gh-wv-${g.order.id}-${d.iso}`"
                  class="job-card job-ghost"
                  :class="statusClass(g.order.status)"
                  :title="ghostTitle(g)"
                  @click="openOrder(g.order.id)"
                >
                  <div class="ghost-head">
                    <i class="fas fa-angles-right"></i>
                    <strong>{{ g.order.order_number }}</strong>
                    <span class="ghost-rest">{{ fmtHM(g.hours) }}</span>
                  </div>
                  <div class="ghost-sub">Überläufer vom {{ shortDate(g.fromIso) }}</div>
                </div>

                <div
                  v-if="cellHasContent(m.code, d.iso, 'wv')"
                  class="day-load"
                  :class="`load-${dayLoad(m.code, d.iso, 'wv').level}`"
                  :title="dayLoadTitle(m.code, d.iso, 'wv')"
                >
                  <span class="dl-bar"><span class="dl-bar-fill" :style="{ width: Math.min(100, dayLoad(m.code, d.iso, 'wv').pct) + '%' }"></span></span>
                  <span class="dl-text">
                    <strong>{{ fmtHM(dayLoad(m.code, d.iso, 'wv').hours) }}</strong>/{{ dayLoad(m.code, d.iso, 'wv').capacity }}h
                    <em>{{ dayLoad(m.code, d.iso, 'wv').pct }}%</em>
                  </span>
                </div>
                <div v-if="!cellHasContent(m.code, d.iso, 'wv')" class="cell-empty"></div>
              </div>
            </div>

            <!-- Tageweise Summierung, WV-Lane -->
            <div class="row row-sum">
              <div class="cell-machine cell-sum-label" :title="`Tagessummen ${m.display}: fertige Aufträge, Formen, Druckanzahl`">
                <i class="fas fa-calculator"></i> Summe
              </div>
              <div
                v-for="d in workDays"
                :key="`wvsum-${m.code}-${d.iso}`"
                class="cell-day cell-sum"
                :class="{ today: d.isToday, weekend: d.isWeekend, past: d.isPast, narrow: d.isNarrow, filled: !!dailyTotal(m.code, d.iso) }"
                :style="{ flexGrow: d.flexGrow }"
                :title="dailyTotalTitle(m.code, d.iso)"
              >
                <template v-if="dailyTotal(m.code, d.iso)">
                  <span class="sum-part sum-jobs"><i class="fas fa-check"></i>{{ dailyTotal(m.code, d.iso).fertige_auftraege }}</span>
                  <span class="sum-part sum-forms"><i class="fas fa-stamp"></i>{{ dailyTotal(m.code, d.iso).formen }}</span>
                  <span class="sum-part sum-run">{{ fmtQty(dailyTotal(m.code, d.iso).druckanzahl) }}</span>
                </template>
                <span v-else class="sum-none">–</span>
              </div>
            </div>
            </template>

            <!-- #4 Überfällige WV-Aufträge: unten in grau sichtbar -->
            <div v-if="overdueByKind.wv.length" class="row row-overdue">
              <div class="cell-machine cell-overdue">
                <i class="fas fa-clock-rotate-left"></i> Überfällig
              </div>
              <div class="cell-day cell-overdue-list">
                <div
                  v-for="o in overdueByKind.wv"
                  :key="`od-wv-${o.id}`"
                  class="job-card job-card-wv overdue-strip"
                  :class="statusClass(o.status)"
                  @click="openOrder(o.id)"
                  :title="`${o.order_number} · ${o.customer_name} · überfällig seit ${overdueWhen(o, 'wv')}`"
                >
                  <div class="job-line-1">
                    <strong>{{ o.order_number }}</strong>
                    <order-flags :order="o" />
                  </div>
                  <div class="overdue-when"><i class="fas fa-calendar-xmark"></i> {{ overdueWhen(o, 'wv') }}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Vertical resize handle between WV (left) and Alerts (right) -->
        <div
          class="resize-handle resize-v"
          :class="{ active: resize === 'v' }"
          title="Breite des WV-Plans ziehen"
          @mousedown.prevent="beginResize('v', $event)"
        ></div>

        <!-- C) Akut überfällig + Hinweise (rechts) -->
        <section class="kp-panel kp-alerts" :class="{ 'is-resizing': resize === 'v' }">
          <header class="panel-head panel-head-alert">
            <i class="fas fa-triangle-exclamation"></i>
            <span>Akut überfällig</span>
            <small class="panel-info">Prio 1+2, sortiert nach Priorität</small>
          </header>

          <div class="alerts-list">
            <div v-if="!alerts.length" class="alerts-empty">
              <i class="fas fa-check-circle"></i>
              Keine überfälligen Prio-1/2 Aufträge.
            </div>

            <div
              v-for="o in alerts"
              :key="`al-${o.id}`"
              class="alert-row"
              :class="`alert-prio-${o.priority}`"
              @click="openOrder(o.id)"
            >
              <div class="alert-top">
                <span class="alert-prio-pill" :class="`alert-prio-${o.priority}`">
                  {{ o.priority === 'urgent' ? 'P1' : 'P1' }}
                </span>
                <strong>{{ o.order_number }}</strong>
                <order-flags :order="o" />
              </div>
              <div class="alert-cust">{{ o.customer_name }}</div>
              <div class="alert-meta">
                <span class="alert-deadline" :class="{ past: isPast(o.delivery_date) }">
                  <i class="fas fa-calendar"></i>
                  {{ o.delivery_date || '–' }}
                </span>
                <span v-if="o.maschine_name" class="alert-machine">
                  <i class="fas fa-print"></i>
                  {{ displayMachine(o.maschine_name) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Hinweise / Hand-Kommentare -->
          <header class="panel-head panel-head-notes">
            <i class="fas fa-sticky-note"></i>
            <span>Hinweise &amp; Hand-Kommentare</span>
          </header>
          <div class="notes-list">
            <div v-if="!notesOrders.length" class="alerts-empty">
              <i class="fas fa-comment-slash"></i>
              Keine aktiven Kommentare.
            </div>
            <div
              v-for="o in notesOrders"
              :key="`nt-${o.id}`"
              class="note-row"
              @click="openOrder(o.id)"
            >
              <div class="note-top">
                <strong>{{ o.order_number }}</strong>
                <span class="note-cust">{{ o.customer_name }}</span>
              </div>
              <div class="note-text">{{ truncate(o.notes, 120) }}</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import OrderFlags from '@/components/OrderFlags.vue'
import FarbigkeitSquare from '@/components/FarbigkeitSquare.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import { displayMachineName, setMachineRegistry, laneIsActive } from '@/utils/machineNames'
import { fmtHM as fmtHMUtil, progressPct, isOvertime as isOvertimeUtil, isOver120 as isOver120Util, overtimeHours as overtimeHoursUtil } from '@/utils/timeCalc'
import {
  SHIFT_HOURS,
  MAX_JOBS_PER_SHIFT,
  activeShifts,
  shiftForAssignment,
  dailyCapacityHours,
  loadPct,
  loadLevel,
  isOverbooked,
  distributeOverflow,
  hoursLeftInDay,
  hourPxFor,
  HOUR_PX_FALLBACK,
  LOAD_LABEL
} from '@/utils/shift'

// ─── Machine catalogue — FALLBACK (per #9 / #10 / #40) ─────────────────────
// Die Lanes kommen jetzt live aus der Maschinen-API (getMachineList(), Kategorie
// druck/wv, kap_pl_relevant, nach position). Diese statischen Arrays dienen nur
// als Fallback, wenn die Registry (noch) leer ist (erster Paint/offline), damit
// das Board nie leer bleibt. `aliases` routen Prinance-/Legacy-Namen zur Lane.
const DRUCK_MACHINES = [
  { code: '102er Offs.', display: '102er Offs.', aliases: ['CD102-K1', 'CD102', '102er Offs.'] },
  { code: '75er Offs.',  display: '75er Offs.',  aliases: ['CD75-5T',  'CD754-5T', 'CD75', '75er Offs.'] },
  { code: 'Offset X',    display: 'Offset X',    aliases: ['Offset X'] },                                   // Handauswahl
  { code: 'Digital A',   display: 'Digital A',   aliases: ['Digital A', 'LINO_CV', 'LINO'] },
  { code: 'Digital B',   display: 'Digital B',   aliases: ['Digital B', 'Digital'] },
  { code: 'Digital C',   display: 'Digital C',   aliases: ['Digital C', 'Versafire3', 'Versafire'] },
  { code: 'Digital X',   display: 'Digital X',   aliases: ['Digital X'] },                                  // Handauswahl
  { code: 'Extern (X)',  display: 'Extern (X)',  aliases: ['Extern (X)', 'Extern', 'X', 'extern'] }
]

const WV_MACHINES = [
  { code: 'Stitch-Liner HX', display: 'Stitch-Liner HX', aliases: ['Stitch-Liner HX', 'STILO', 'SAMMELH', 'Sammelh.'] }, // STILO→HX default, D manuell
  { code: 'Stitch-Liner D',  display: 'Stitch-Liner D',  aliases: ['Stitch-Liner D'] },
  { code: 'Binder',          display: 'Binder',          aliases: ['Binder', 'Klebebinder'] },
  { code: 'Binder ext',      display: 'Binder ext',      aliases: ['Binder ext', 'Klebebinder extern'] },
  { code: 'Fold 78',         display: 'Fold 78',         aliases: ['Fold 78', 'Falz 79', 'FALZ'] },
  { code: 'Fold 55',         display: 'Fold 55',         aliases: ['Fold 55', 'Falz 55'] },
  { code: 'WV ex',           display: 'WV ex',           aliases: ['WV ex', 'Bubi extern'] },
  { code: 'Handwork',        display: 'Handwork',        aliases: ['Handwork'] },
  { code: 'Sonst',           display: 'Sonst',           aliases: ['Sonst', 'sonstige'] }
]

// Req #8 — an order can run on several machines at once. The API returns a
// `machine_assignments` array (each with its own maschine_name + planning
// window). Fall back to the legacy single `maschine_name` for older payloads.
function machineAssignments(o) {
  if (Array.isArray(o.machine_assignments) && o.machine_assignments.length) {
    return o.machine_assignments
  }
  return o.maschine_name ? [{ maschine_name: o.maschine_name }] : []
}


// Parse the formats the Rails API returns:
//   - "YYYY-MM-DD"            (controller#format_date)
//   - "YYYY-MM-DD HH:MM"      (with hour)
//   - "DD/MM/YYYY [HH:MM]"    (model#delivery_date legacy)
//   - "DD.MM.YYYY [HH:MM]"    (de-DE display)
// → local-midnight Date or null.
function parseDmy(s) {
  if (!s) return null
  const str = String(s).trim()
  let m
  if ((m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/))) {
    const d = new Date(+m[1], +m[2] - 1, +m[3])
    return isNaN(d.getTime()) ? null : d
  }
  if ((m = str.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/))) {
    const d = new Date(+m[3], +m[2] - 1, +m[1])
    return isNaN(d.getTime()) ? null : d
  }
  return null
}
function isoDay(d) {
  if (!d) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
// Local "YYYY-MM-DD HH:MM:SS" (no UTC shift) for datetime columns like
// order_machine_assignments.geplant_start — Rails parses it as local time.
function toDateTimeStr(d) {
  if (!d) return null
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:00`
}

export default {
  name: 'KapazitaetsplanungTri',
  components: { OrderFlags, FarbigkeitSquare, UserAvatar },

  data() {
    return {
      // Live-Uhr für den Status-3-Laufzeit-Timer (im Minutentakt aktualisiert).
      now: Date.now(),
      _clock: null,
      // Board lanes: only orders with a machine, in the visible week.
      orders: [],
      // Backend machine master-table (#40). Drives which lanes are shown — a
      // lane whose machines are all unchecked ("Aktiv" off) is hidden.
      machines: [],
      // Sidebar (Akut/Überfällig + Hinweise): undelivered, dated orders.
      activeOrders: [],
      loading: false,
      // Anchor for the week being shown — Monday-of-week of `today` by default.
      anchor: this.startOfWeek(new Date()),
      // Resizable panel split. fr values that sum to a fixed total so the
      // proportional split is preserved on window resize.
      //   topFr  : Druck row vs. (WV + Alerts) row, total = 2
      //   leftFr : WV col vs. Alerts col,           total = 2.4
      // Restored from localStorage in mounted().
      topFr: 1,
      leftFr: 1.4,
      // 'h' while dragging horizontal handle, 'v' for vertical, null otherwise.
      resize: null,
      _resizeMove: null,
      _resizeUp: null,
      // Drag-to-reschedule state (owner/manager only).
      dragOrder: null,   // the order being dragged
      dragKind: null,    // 'druck' | 'wv' — drops only land in the same panel
      dragCode: null,    // source lane code of the dragged card (#8 assignment match)
      dragOverKey: null, // "kind|machineCode|iso" of the hovered drop cell
      dragOverCode: null, // machine code of the hovered cell — highlights the whole row
      dragOverIso: null,  // iso date of the hovered cell — highlights the whole column
      // Betriebsmodus des Standorts: 1 = eine Schicht (8 h/Tag), 2 = zwei
      // Schichten (16 h/Tag). Kommt aus /settings; 2 ist die Chef-Vorgabe.
      shiftSystem: 2,
      // Gemeldete Stückzahlen aus dem Schicht-Protokoll (#16), aggregiert:
      //   "maschine_name|YYYY-MM-DD|schicht" -> Stück
      producedByShift: {},
      // 'shift' | 'day' — ab wann ein Auftrag auf den Folgetag überläuft.
      // Chef 17.09.2026: Default 'day' — erst überlaufen, wenn auch die
      // Spätschicht voll ist.
      overflowThreshold: 'day',
      // Tagessummen je Maschine (08.06.2026): "code|YYYY-MM-DD" -> Summenzeile.
      dailyTotals: {},
      // Gemessene Höhe des Druck-Panels — Grundlage für die viewport-relative
      // Schichthöhe (16.06. #3). 0 = noch nicht gemessen.
      druckPanelHeight: 0,
      _panelObserver: null,
    }
  },

  computed: {
    // Lanes live aus der Maschinen-API (this.machines): Kategorie druck/wv, nur
    // kap_pl_relevant, nach position. Fallback = statische Arrays, solange die
    // Registry (noch) leer ist (erster Paint/offline) → nie leeres Board (#40).
    laneDefs() {
      const build = (cat, fallback) => {
        const rows = (this.machines || []).filter(m => m.category === cat && m.kap_pl_relevant !== false)
        if (!rows.length) return fallback
        return rows
          .slice()
          .sort((a, b) => (a.position || 0) - (b.position || 0))
          .map(m => ({
            code: m.display_name,
            display: m.display_name,
            aliases: [m.display_name, m.code, ...(m.aliases || [])].filter(Boolean)
          }))
      }
      return { druck: build('druck', DRUCK_MACHINES), wv: build('wv', WV_MACHINES) }
    },
    // alias|code|display (lowercased) → { kind, code } — für canonicalMachine().
    aliasIndex() {
      const idx = new Map()
      const add = (lanes, kind) => lanes.forEach(m =>
        (m.aliases || []).forEach(a => idx.set(String(a).toLowerCase().trim(), { kind, code: m.code })))
      add(this.laneDefs.druck, 'druck')
      add(this.laneDefs.wv, 'wv')
      return idx
    },
    // Lanes drop out, wenn alle zugehörigen Maschinen "Aktiv" aus sind.
    // Zerlegt die Tageszelle in Schicht-Blöcke? Nur im 2-Schicht-Betrieb —
    // sonst wäre die Kopfzeile pure Dekoration.
    splitByShift() {
      return Number(this.shiftSystem) === 2
    },

    maxJobsPerShift() {
      return MAX_JOBS_PER_SHIFT
    },

    // Was passt an einem Folgetag hinein? Im Modus 'day' ein ganzer Tag
    // (8 bzw. 16 h), im Modus 'shift' eine Schicht.
    //
    // Entschieden 17.09.2026 (Chef): 'day' — ein Auftrag läuft erst auf den
    // Folgetag über, wenn er auch in der Spätschicht keinen Platz mehr hat.
    // Die Quelle 08.06.2026.md war widersprüchlich (:84 vs :87); 'shift'
    // bleibt als Schalter erhalten.
    overflowThresholdHours() {
      return this.overflowThreshold === 'shift'
        ? SHIFT_HOURS
        : dailyCapacityHours(this.shiftSystem)
    },

    // px je Stunde für die Kartenhöhe. Vorgabe 16.06. #3: eine 8-h-Schicht ist
    // so hoch wie eine Maschinenzeile, wenn VIER Maschinen den Viewport füllen.
    // Vor der ersten Messung greift der bisherige Festwert.
    hourPx() {
      return hourPxFor(this.druckPanelHeight) || HOUR_PX_FALLBACK
    },

    druckMachines() {
      return this.laneDefs.druck.filter(m => laneIsActive(m.aliases))
    },
    wvMachines() {
      return this.laneDefs.wv.filter(m => laneIsActive(m.aliases))
    },

    workDays() {
      // 8-day window: Monday of the anchored week through the following Monday
      // (inclusive). Weekends ARE kept (wishlist #53: Sa/So as narrow 1/4
      // placeholders for the rare emergency shift, not hidden). shiftWeek()
      // moves the anchor ±7 days; windows overlap on the shared Monday.
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const todayIso = isoDay(today)
      const todayMs = today.getTime()
      const yest = new Date(today)
      yest.setDate(today.getDate() - 1)
      const yesterdayIso = isoDay(yest)

      // Which isos carry at least one job (across Druck + WV) — drives the
      // "empty day = 1/2 width" rule (#53/#6). indexByMachineDay depends only
      // on `orders`, so this is not circular.
      const jobDays = new Set()
      for (const kind of ['druck', 'wv']) {
        for (const key of Object.keys(this.indexByMachineDay[kind])) {
          if (this.indexByMachineDay[kind][key].length) jobDays.add(key.split('|')[1])
        }
      }

      const start = new Date(this.anchor)
      start.setHours(0, 0, 0, 0)
      const days = []
      for (let i = 0; i < 8; i++) {
        const d = new Date(start)
        d.setDate(start.getDate() + i)       // setDate avoids DST hour-drift
        const dow = d.getDay()               // 0=Sun … 6=Sat
        // Kundenwunsch (Client 2026-07): Sonntage komplett weglassen.
        if (dow === 0) continue
        const iso = isoDay(d)
        const isToday = iso === todayIso
        const isWeekend = dow === 6          // nur noch Samstag (So entfällt)
        const isPast = d.getTime() < todayMs
        const hasJobs = jobDays.has(iso)

        // Breiten-Priorität (Client 2026-07): heute am breitesten → Samstag &
        // Tage ohne Einträge 1/4 (Sa mit Notfall-Schicht 1/2) → vergangener
        // Arbeitstag 2/3 → normaler künftiger Arbeitstag voll.
        // Ausnahme (Client 2026-07-17): heute OHNE Aufträge wird nicht breit
        // aufgezogen, sondern schmal wie jeder andere Leertag (1/4).
        let flexGrow = 1
        if (isToday) flexGrow = hasJobs ? 1.7 : 0.25
        else if (isWeekend) flexGrow = hasJobs ? 0.5 : 0.25
        else if (!hasJobs) flexGrow = 0.25   // Tage ohne Einträge = 1/4 Breite
        else if (isPast) flexGrow = 0.66

        days.push({
          iso,
          date: d,
          dayName: d.toLocaleDateString('de-DE', { weekday: 'short' }),
          dayNumber: d.getDate(),
          // Korrektes deutsches Datum, eine Zeile: "Mo, 22. Juni" (#53).
          dateLabel: `${d.toLocaleDateString('de-DE', { weekday: 'short' })}, ${d.getDate()}. ${d.toLocaleDateString('de-DE', { month: 'long' })}`,
          // Kompakt für schmale Wochenend-/Leertag-Spalten: "Sa 22."
          dateLabelShort: `${d.toLocaleDateString('de-DE', { weekday: 'short' })} ${d.getDate()}.`,
          monthName: d.toLocaleDateString('de-DE', { month: 'short' }),
          isToday,
          isYesterday: iso === yesterdayIso,
          isWeekend,
          isPast,
          hasJobs,
          // Schmal, wenn Leertag (auch heute ohne Aufträge) oder Samstag.
          isNarrow: !hasJobs || isWeekend,
          flexGrow
        })
      }
      return days
    },

    weekLabel() {
      const first = this.workDays[0]
      const last  = this.workDays[this.workDays.length - 1]
      if (!first || !last) return ''
      const f = first.date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
      const l = last.date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
      return `${f} – ${l}`
    },

    // Only Kap.Pl. admins (owner/manager) may reschedule orders on the board.
    // Read the REACTIVE store user first: checkAuth() (App.vue, on load) refreshes
    // the role asynchronously, and a fresh login updates it too. Reading
    // localStorage directly made this a stale, non-reactive value — if the board
    // rendered before checkAuth returned, canMove cached `false` for the whole
    // session and cards stayed draggable="false" ("clicks instead of drags").
    // localStorage stays as a fallback for the very first paint.
    canMove() {
      // Fähigkeit „plan_capacity" (A-/B-Admin + Kalkulator) — reaktiv aus dem Store.
      return this.$store.getters.can('plan_capacity')
    },

    // Group orders by (machineCode, isoDay) so jobsAt() is O(1).
    indexByMachineDay() {
      const idx = { druck: {}, wv: {} }
      for (const o of this.orders) {
        // Req #8 — one order can sit on several lanes simultaneously. Place it
        // once per machine assignment; the same order object is pushed into each
        // lane's cell (drag-drop keeps working on object identity).
        for (const a of machineAssignments(o)) {
          const { kind, code } = this.canonicalMachine(a.maschine_name)
          if (!kind) continue
          const ref = this.refDayFor(o, a, kind)
          if (!ref) continue
          const day = isoDay(ref)
          const key = `${code}|${day}`
          idx[kind][key] = idx[kind][key] || []
          if (!idx[kind][key].includes(o)) idx[kind][key].push(o)
        }
      }
      // Order each cell: forgotten (overdue, not done) on top, ready (done) at
      // the bottom, everything else in between — see isForgotten()/isReady().
      const rank = (o, iso) => this.isForgotten(o, iso) ? 0 : (this.isReady(o) ? 2 : 1)
      for (const kind of ['druck', 'wv']) {
        for (const key of Object.keys(idx[kind])) {
          const iso = key.split('|')[1]
          idx[kind][key].sort((a, b) => rank(a, iso) - rank(b, iso))
        }
      }
      return idx
    },

    // ── Überläufer-Index (08.06.2026.md:81-87) ──────────────────────────────
    // „Wenn Gesamtzeit > 8h: automatische Darstellung in Folgeschicht/Folgetag
    //  mit Höhe = Reststundenzahl.“
    //
    // Die Merkung wird NICHT gespeichert, sondern hier aus kalkulierter Zeit +
    // Status berechnet. Damit erledigen sich zwei Vorgaben von selbst:
    //   „fertig am Tag 1 → Merkung am Folgetag automatisch gelöscht“ — es gibt
    //   nichts zu löschen, sie wird schlicht nicht mehr berechnet;
    //   „Job-Daten im Hintergrund zusammenlegen“ — es gab nie zwei Datensätze.
    //
    // Schlüssel wie indexByMachineDay: "machineCode|iso" -> [{ order, hours }]
    overflowIndex() {
      const idx = { druck: {}, wv: {} }
      const days = this.workDays
      if (!days.length) return idx

      const isoList = days.map(d => d.iso)
      const perDay = this.overflowThresholdHours
      if (!(perDay > 0)) return idx

      for (const o of this.orders) {
        // Fertig gemeldet → keine Merkung mehr.
        if (this.overflowDone(o)) continue

        const total = this.calcHours(o)
        if (!(total > 0)) continue

        for (const a of machineAssignments(o)) {
          const { kind, code } = this.canonicalMachine(a.maschine_name)
          if (!kind) continue
          const ref = this.refDayFor(o, a, kind)
          if (!ref) continue

          const startIdx = isoList.indexOf(isoDay(ref))
          if (startIdx < 0) continue

          // Reststunden über die Folgetage verteilen (reine Funktion, getestet).
          // Am Starttag zählt, was ab dem Start noch frei ist; jeder Folgetag
          // fasst einen ganzen Tag.
          const room = this.firstDayRoom(o, a)
          if (!(total > room)) continue
          const blocks = distributeOverflow(total, room, days.length - startIdx - 1, perDay)
          blocks.forEach((hours, n) => {
            const day = days[startIdx + 1 + n]
            if (!day) return
            const key = `${code}|${day.iso}`
            idx[kind][key] = idx[kind][key] || []
            idx[kind][key].push({ order: o, hours, fromIso: isoList[startIdx] })
          })
        }
      }
      return idx
    },

    druckOrdersCount() {
      const days = new Set(this.workDays.map(d => d.iso))
      let n = 0
      for (const key of Object.keys(this.indexByMachineDay.druck)) {
        if (days.has(key.split('|')[1])) n += this.indexByMachineDay.druck[key].length
      }
      return n
    },

    wvOrdersCount() {
      const days = new Set(this.workDays.map(d => d.iso))
      let n = 0
      for (const key of Object.keys(this.indexByMachineDay.wv)) {
        if (days.has(key.split('|')[1])) n += this.indexByMachineDay.wv[key].length
      }
      return n
    },

    // ─── Live-Diagnose: orders → druck / wv / unbekannte Maschine ────────
    druckMatchedCount() {
      let n = 0
      for (const key of Object.keys(this.indexByMachineDay.druck)) {
        n += this.indexByMachineDay.druck[key].length
      }
      return n
    },
    wvMatchedCount() {
      let n = 0
      for (const key of Object.keys(this.indexByMachineDay.wv)) {
        n += this.indexByMachineDay.wv[key].length
      }
      return n
    },
    unmappedMachines() {
      const seen = new Map()
      for (const o of this.orders) {
        const { kind } = this.canonicalMachine(o.maschine_name)
        if (kind) continue
        const name = (o.maschine_name && String(o.maschine_name).trim()) || '(leer)'
        seen.set(name, (seen.get(name) || 0) + 1)
      }
      return Array.from(seen.entries()).map(([n, c]) => `${n} (${c})`)
    },

    alerts() {
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const dead = new Set(['delivered', 'invoice_issued', 'archived'])
      return this.activeOrders
        .filter(o => ['urgent', 'critical'].includes(o.priority))
        .filter(o => !dead.has(o.status))
        .filter(o => {
          const d = parseDmy(o.delivery_date)
          // overdue OR less than 3 days out
          return d && d.getTime() < today.getTime() + 3 * 24 * 60 * 60 * 1000
        })
        .sort((a, b) => {
          // urgent first, then critical; within group: earlier delivery first
          const pw = p => p === 'urgent' ? 0 : 1
          if (pw(a.priority) !== pw(b.priority)) return pw(a.priority) - pw(b.priority)
          const da = parseDmy(a.delivery_date)?.getTime() || Infinity
          const db = parseDmy(b.delivery_date)?.getTime() || Infinity
          return da - db
        })
        .slice(0, 12)
    },

    notesOrders() {
      const dead = new Set(['delivered', 'invoice_issued', 'archived'])
      return this.activeOrders
        .filter(o => !dead.has(o.status) && o.notes && o.notes.trim().length > 5)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 6)
    },

    // #4 — Aufträge, die vor dem sichtbaren Fenster terminiert waren und noch
    // nicht ausgeliefert sind, fallen sonst aus der Kap.Pl. heraus. Sie bleiben
    // unten in einer grauen "Überfällig"-Zeile sichtbar (Stati bleiben farbig).
    overdueByKind() {
      const out = { druck: [], wv: [] }
      const days = this.workDays
      if (!days.length) return out
      // Cutoff = heute (fixe Referenz, unabhängig von der angezeigten Woche).
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const cutoff = today.getTime()
      // Aufträge, die ohnehin in einer sichtbaren Tagesspalte stehen, sind nicht
      // "verloren" — sie werden nicht zusätzlich in der grauen Zeile dupliziert.
      const visibleDays = new Set(days.map(d => d.iso))
      const dead = new Set(['delivered', 'invoice_issued', 'archived', 'cancelled'])
      for (const o of this.activeOrders) {
        if (dead.has(o.status)) continue
        const { kind } = this.canonicalMachine(o.maschine_name)
        if (!kind) continue
        const ref = kind === 'wv'
          ? (parseDmy(o.processing_date) || parseDmy(o.print_date) || parseDmy(o.delivery_date))
          : (parseDmy(o.print_date) || parseDmy(o.delivery_date))
        if (!ref || ref.getTime() >= cutoff) continue
        if (visibleDays.has(isoDay(ref))) continue
        out[kind].push(o)
      }
      const byRef = k => (a, b) => {
        const ra = (k === 'wv'
          ? (parseDmy(a.processing_date) || parseDmy(a.print_date) || parseDmy(a.delivery_date))
          : (parseDmy(a.print_date) || parseDmy(a.delivery_date)))?.getTime() || 0
        const rb = (k === 'wv'
          ? (parseDmy(b.processing_date) || parseDmy(b.print_date) || parseDmy(b.delivery_date))
          : (parseDmy(b.print_date) || parseDmy(b.delivery_date)))?.getTime() || 0
        return ra - rb
      }
      out.druck.sort(byRef('druck'))
      out.wv.sort(byRef('wv'))
      return out
    },

    layoutCssVars() {
      const l = this.clampFr(this.leftFr, 2.4, 0.3)
      return {
        '--num-days': this.workDays.length,
        '--n-druck': this.druckMachines.length,
        '--n-wv': this.wvMachines.length,
        // Panels hug their content (shrink-to-content) — no fixed-height fr split,
        // so sparse weeks no longer leave a big empty gradient below the rows.
        gridTemplateRows: 'auto auto',
        '--bottom-cols': `${l}fr 6px ${2.4 - l}fr`
      }
    }
  },

  mounted() {
    this.restoreLayout()
    this.loadMachines()
    this.loadShiftSettings()
    this.loadOrders()
    this._resizeMove = (e) => this.onResizeMove(e)
    this._resizeUp   = () => this.endResize()
    document.addEventListener('mousemove', this._resizeMove)
    document.addEventListener('mouseup',   this._resizeUp)
    // Status-3-Timer im Minutentakt fortschreiben (laufende Druckzeit).
    this._clock = setInterval(() => { this.now = Date.now() }, 60_000)
    this.observeDruckPanel()
  },

  beforeUnmount() {
    if (this._panelObserver) this._panelObserver.disconnect()
    if (this._clock) clearInterval(this._clock)
    if (this._resizeMove) document.removeEventListener('mousemove', this._resizeMove)
    if (this._resizeUp)   document.removeEventListener('mouseup',   this._resizeUp)
  },

  watch: {
    // Re-fetch the board whenever the user navigates to another week.
    anchor() {
      this.loadBoard()
      this.loadProduced()
      this.loadDailyTotals()
    }
  },

  methods: {
    ...mapActions(['fetchData']),

    // Refresh both data sources (called on mount + the reload button).
    async loadOrders() {
      await Promise.all([this.loadBoard(), this.loadActive(), this.loadProduced(), this.loadDailyTotals()])
    },

    // Höhe des Druck-Panels messen und bei jeder Layout-Änderung (Fenster,
    // Panel-Splitter) nachführen — daraus ergibt sich `hourPx`.
    observeDruckPanel() {
      const el = this.$el && this.$el.querySelector ? this.$el.querySelector('.kp-druck .timeline') : null
      if (!el) return
      const apply = () => { this.druckPanelHeight = el.clientHeight || 0 }
      apply()
      if (typeof ResizeObserver === 'undefined') return
      this._panelObserver = new ResizeObserver(apply)
      this._panelObserver.observe(el)
    },

    // Betriebsmodus (1-/2-Schicht) des Standorts. Fällt still auf 2 zurück —
    // ohne Einstellung ist der 2-Schicht-Betrieb die Chef-Vorgabe.
    async loadShiftSettings() {
      try {
        const resp = await this.fetchData({ endpoint: 'settings' })
        const cfg = resp?.data
        if (cfg && cfg.shift_system) this.shiftSystem = Number(cfg.shift_system)
        if (cfg && cfg.overflow_threshold) this.overflowThreshold = cfg.overflow_threshold
      } catch (e) {
        console.error('Kap.Pl. Schicht-Einstellung konnte nicht geladen werden:', e)
      }
    },

    // Tagessummen je Maschine für das sichtbare Fenster. Das Backend rechnet
    // live aus den Aufträgen, hier wird nur auf die Lane normalisiert.
    async loadDailyTotals() {
      const days = this.workDays
      if (!days.length) return
      try {
        const resp = await this.fetchData({
          endpoint: 'machine_daily_totals',
          params: { von: days[0].iso, bis: days[days.length - 1].iso }
        })
        const map = {}
        for (const row of (resp?.data || [])) {
          const { code } = this.canonicalMachine(row.maschine_name)
          if (!code) continue
          const key = `${code}|${row.datum}`
          const acc = map[key] || { fertige_auftraege: 0, formen: 0, druckanzahl: 0, produzierte_stueck: 0 }
          acc.fertige_auftraege += Number(row.fertige_auftraege || 0)
          acc.formen            += Number(row.formen || 0)
          acc.druckanzahl       += Number(row.druckanzahl || 0)
          acc.produzierte_stueck += Number(row.produzierte_stueck || 0)
          map[key] = acc
        }
        this.dailyTotals = map
      } catch (e) {
        console.error('Kap.Pl. Tagessummen konnten nicht geladen werden:', e)
      }
    },

    // Summenzeile für Maschine/Tag — null, wenn an dem Tag nichts fertig wurde.
    dailyTotal(machineCode, isoDay) {
      const t = this.dailyTotals[`${machineCode}|${isoDay}`]
      return t && t.fertige_auftraege > 0 ? t : null
    },

    dailyTotalTitle(machineCode, isoDay) {
      const t = this.dailyTotal(machineCode, isoDay)
      const name = this.displayMachine(machineCode)
      if (!t) return `${name} — an diesem Tag wurde nichts fertig gemeldet`
      const parts = [
        `${name} — ${t.fertige_auftraege} Auftrag/Aufträge fertig`,
        `${t.formen} Formen/Durchgänge`,
        `${this.fmtQty(t.druckanzahl)} Druckanzahl (kalkuliert)`
      ]
      if (t.produzierte_stueck) parts.push(`${this.fmtQty(t.produzierte_stueck)} Stück gemeldet`)
      return parts.join(' · ')
    },

    // #16 — gemeldete Stückzahlen je Maschine/Tag/Schicht für das sichtbare
    // Fenster. Eine Abfrage fürs ganze Board (Backend gruppiert), kein N+1.
    async loadProduced() {
      const days = this.workDays
      if (!days.length) return
      try {
        const resp = await this.fetchData({
          endpoint: 'shift_productions/summary',
          params: { von: days[0].iso, bis: days[days.length - 1].iso }
        })
        const map = {}
        for (const row of (resp?.data || [])) {
          // Das Protokoll speichert den rohen Maschinennamen — auf die Lane
          // normalisieren, sonst findet die Zelle ihre Zahl nicht.
          const { code } = this.canonicalMachine(row.maschine_name)
          if (!code || !row.schicht) continue
          const key = `${code}|${row.datum}|${row.schicht}`
          map[key] = (map[key] || 0) + Number(row.stueck || 0)
        }
        this.producedByShift = map
      } catch (e) {
        console.error('Kap.Pl. Schicht-Protokoll konnte nicht geladen werden:', e)
      }
    },

    // Machine master-table → decides which lanes are visible. Unchecked
    // ("Aktiv" off) machines drop their lane from the board.
    async loadMachines() {
      try {
        const resp = await this.fetchData({ endpoint: 'machines' })
        const list = resp?.data || []
        setMachineRegistry(list)   // populate ACTIVE before triggering the recompute
        this.machines = list
      } catch (e) {
        console.error('Kap.Pl. machine list load failed:', e)
      }
    },

    // Prinance-/Legacy-Maschinenname → { kind: 'druck'|'wv', code } aus der
    // Registry (aliasIndex); null, wenn keiner Lane zuordenbar.
    canonicalMachine(raw) {
      if (!raw) return { kind: null, code: null }
      return this.aliasIndex.get(String(raw).toLowerCase().trim()) || { kind: null, code: null }
    },

    // Board: machine-assigned orders whose ref date falls in the visible week.
    async loadBoard() {
      const days = this.workDays
      if (!days.length) return
      this.loading = true
      try {
        const resp = await this.fetchData({
          endpoint: 'orders',
          params: {
            page: 1,
            per_page: 1000,
            with_machine: 1,
            week_start: days[0].iso,
            week_end: days[days.length - 1].iso
          }
        })
        this.orders = resp?.data || []
      } catch (e) {
        console.error('Kap.Pl. board load failed:', e)
      } finally {
        this.loading = false
      }
    },

    // Sidebar pool: undelivered orders with a real date, soonest/overdue first.
    // alerts() and notesOrders() filter this down further.
    async loadActive() {
      try {
        const resp = await this.fetchData({
          endpoint: 'orders',
          params: {
            page: 1,
            per_page: 200,
            undelivered: 1,
            dated: 1,
            sort: 'delivery_date',
            direction: 'asc'
          }
        })
        this.activeOrders = resp?.data || []
      } catch (e) {
        console.error('Kap.Pl. sidebar load failed:', e)
      }
    },

    startOfWeek(d) {
      // Monday-anchor (de-DE convention).
      // Mon–Sat → Monday of THIS week. Sun → Monday of NEXT week (Sunday
      // is not a working day, so users mean the upcoming week).
      const x = new Date(d)
      x.setHours(0, 0, 0, 0)
      const dow = x.getDay()           // 0=Sun … 6=Sat
      const delta = dow === 0 ? 1 : 1 - dow
      x.setDate(x.getDate() + delta)
      return x
    },

    shiftWeek(weeks) {
      const n = new Date(this.anchor)
      n.setDate(n.getDate() + weeks * 7)
      this.anchor = n
    },

    // Tage-Verschiebung (Chef 10.06.): das 8-Tage-Fenster um ±1 Tag schieben.
    // Anders als shiftWeek bleibt der Anker nicht Montag-gebunden — gewollt, der
    // Chef will Tag für Tag durchblättern können.
    shiftDay(days) {
      const n = new Date(this.anchor)
      n.setDate(n.getDate() + days)
      this.anchor = n
    },

    goToday() {
      this.anchor = this.startOfWeek(new Date())
    },

    // Bezugstag einer Maschinen-Zuordnung. Jede Zuordnung kann ihren eigenen
    // Planbeginn tragen; sonst platziert die Druck-Lane nach Drucktermin, die
    // WV-Lane nach Weiterverarbeitungs-Datum (beide notfalls Liefertermin).
    refDayFor(o, a, kind) {
      return kind === 'wv'
        ? (parseDmy(a.geplant_start) || parseDmy(o.processing_date) || parseDmy(o.print_date) || parseDmy(o.delivery_date))
        : (parseDmy(a.geplant_start) || parseDmy(o.print_date) || parseDmy(o.delivery_date))
    },

    jobsAt(machineCode, isoDay, kind = 'druck') {
      return this.indexByMachineDay[kind][`${machineCode}|${isoDay}`] || []
    },

    // ── Überläufer (08.06.2026.md „Überläufer“) ─────────────────────────────
    // Ein Auftrag gilt als fertig gemeldet, sobald an der Maschine ein Ende
    // erfasst wurde — dann verschwindet die Merkung am Folgetag von selbst
    // („wird dort fertig gemeldet und löscht sich für den nächsten Tag“).
    overflowDone(o) {
      return !!(o && o.tatsaechlich_ende) || this.isReady(o)
    },

    // Merkungen auf den Folgetagen für diese Lane/diesen Tag.
    ghostsAt(machineCode, isoDay, kind = 'druck') {
      return this.overflowIndex[kind][`${machineCode}|${isoDay}`] || []
    },

    // ── Schicht-Gruppierung einer Tageszelle (#16 / „2-Schicht-Toggle“) ──────
    // Liefert die Blöcke, in die eine Zelle zerfällt. Im 1-Schicht-Betrieb ist
    // das GENAU EINE Gruppe ohne Kopfzeile — das Board sieht dann exakt aus wie
    // vorher. Im 2-Schicht-Betrieb kommt je Schicht ein Block mit Kopfzeile:
    // Name, Zeitfenster, belegte Stunden gegen die 8-h-Kapazität, gemeldete
    // Stückzahl und ein Auslastungsbalken.
    //
    // Aufträge ohne Uhrzeit im Planfenster landen in einer eigenen Gruppe
    // „ohne Schicht“ — sie sind auf den Tag geplant, aber noch nicht
    // eingeteilt. Sie stillschweigend in die Frühschicht zu kippen würde die
    // Auslastung verfälschen, und genau die soll der Chef ablesen können.
    shiftGroups(machineCode, isoDay, kind = 'druck') {
      const jobs = this.jobsAt(machineCode, isoDay, kind)
      if (!jobs.length) return []

      const buckets = new Map()
      for (const s of activeShifts(this.shiftSystem)) buckets.set(s.nr, [])
      const unassigned = []

      for (const o of jobs) {
        const nr = this.shiftOfJob(o, machineCode, kind)
        if (nr && buckets.has(nr)) buckets.get(nr).push(o)
        else unassigned.push(o)
      }

      const groups = []
      for (const def of activeShifts(this.shiftSystem)) {
        const list = buckets.get(def.nr) || []
        // Leere Schichten zeigen wir nur im 2-Schicht-Betrieb — dort ist „S2
        // ist frei“ eine Aussage. Im 1-Schicht-Betrieb wäre es nur Ballast.
        if (!list.length && !this.splitByShift) continue
        groups.push(this.buildShiftGroup(def, list, machineCode, isoDay))
      }

      if (unassigned.length) {
        groups.push(this.buildShiftGroup(null, unassigned, machineCode, isoDay))
      }

      return groups
    },

    // Ein Schicht-Block: Auslastung + Kapazität sind die Hauptaussage
    // (Stunden gegen 8 h, Aufträge gegen 6, Ampelfarbe).
    buildShiftGroup(def, list, machineCode, isoDay) {
      const hours = list.reduce((sum, o) => sum + this.calcHours(o), 0)
      const level = loadLevel(hours, SHIFT_HOURS)
      const overCount = isOverbooked(list.length)

      if (!def) {
        return {
          key: 'none',
          shift: null,
          jobs: list,
          hours,
          capacity: SHIFT_HOURS,
          pct: loadPct(hours, SHIFT_HOURS),
          level: 'none',
          overCount,
          produced: 0,
          kurz: '?',
          zeit: 'ohne Schicht',
          title: `${list.length} Auftrag/Aufträge ohne Uhrzeit im Planfenster — noch keiner Schicht zugeteilt`
        }
      }

      const produced = this.producedAt(machineCode, isoDay, def.nr)
      const parts = [
        `${def.label} (${def.zeit})`,
        `${this.fmtHM(hours)} von ${SHIFT_HOURS} h belegt — ${LOAD_LABEL[level]}`,
        `${list.length} von ${MAX_JOBS_PER_SHIFT} Aufträgen`
      ]
      if (overCount) parts.push(`ACHTUNG: mehr als ${MAX_JOBS_PER_SHIFT} Aufträge in einer Schicht`)
      if (produced) parts.push(`${this.fmtQty(produced)} Stück gemeldet`)

      return {
        key: `s${def.nr}`,
        shift: def.nr,
        jobs: list,
        hours,
        capacity: SHIFT_HOURS,
        pct: loadPct(hours, SHIFT_HOURS),
        level,
        overCount,
        produced,
        kurz: def.kurz,
        zeit: def.zeit,
        title: parts.join(' · ')
      }
    },

    // Tages-Auslastung einer Maschine über alle Schichten — die Zahl, die der
    // Chef sucht: „ist die Maschine an dem Tag voll?“
    dayLoad(machineCode, isoDay, kind = 'druck') {
      const jobs = this.jobsAt(machineCode, isoDay, kind)
      const ghosts = this.ghostsAt(machineCode, isoDay, kind)
      // Übergelaufene Stunden belegen die Maschine am Folgetag wirklich —
      // sie MÜSSEN in die Auslastung, sonst sieht der Tag frei aus, obwohl er
      // schon halb vergeben ist. Das ist der eigentliche Zweck der Merkung.
      const hours = jobs.reduce((sum, o) => sum + this.calcHours(o), 0) +
                    ghosts.reduce((sum, g) => sum + g.hours, 0)
      const capacity = dailyCapacityHours(this.shiftSystem)
      return {
        hours,
        capacity,
        jobs: jobs.length + ghosts.length,
        ghosts: ghosts.length,
        pct: loadPct(hours, capacity),
        level: loadLevel(hours, capacity)
      }
    },

    // Schicht EINER Karte auf DIESER Lane. Ein Auftrag kann auf mehreren
    // Maschinen liegen (#8); maßgeblich ist das Planfenster der Zuordnung, die
    // zu dieser Lane gehört.
    shiftOfJob(o, machineCode, kind = 'druck') {
      for (const a of machineAssignments(o)) {
        const canon = this.canonicalMachine(a.maschine_name)
        if (canon.kind !== kind || canon.code !== machineCode) continue
        const nr = shiftForAssignment(a)
        if (nr) return nr
      }
      // Fallback auf das Planfenster des Auftrags. In den Daten hängt die
      // Uhrzeit heute fast immer am Auftrag (`orders.geplant_start`) und nicht
      // an der Maschinen-Zuordnung — ohne diesen Zweig landete praktisch jede
      // Karte in „ohne Schicht“. calcHours() liest dieselbe Quelle, damit
      // Stunden und Schicht aus demselben Fenster kommen.
      return shiftForAssignment({ geplant_start: o.geplant_start })
    },

    // Gemeldete Stückzahl aus dem Schicht-Protokoll für Maschine/Tag/Schicht.
    producedAt(machineCode, isoDay, schicht) {
      return this.producedByShift[`${machineCode}|${isoDay}|${schicht}`] || 0
    },

    fmtQty(v) {
      return new Intl.NumberFormat('de-DE').format(Number(v) || 0)
    },

    dayLoadTitle(machineCode, isoDay, kind = 'druck') {
      const l = this.dayLoad(machineCode, isoDay, kind)
      const parts = [
        `${this.displayMachine(machineCode)} — ${this.fmtHM(l.hours)} von ${l.capacity} h belegt (${l.pct} %)`,
        `${l.jobs} Auftrag/Aufträge`
      ]
      if (l.ghosts) parts.push(`davon ${l.ghosts} Überläufer vom Vortag`)
      parts.push(LOAD_LABEL[l.level] || '')
      return parts.filter(Boolean).join(', ')
    },

    // Zelle hat Inhalt, wenn echte Aufträge ODER Überläufer darin liegen.
    cellHasContent(machineCode, isoDay, kind = 'druck') {
      return this.jobsAt(machineCode, isoDay, kind).length > 0 ||
             this.ghostsAt(machineCode, isoDay, kind).length > 0
    },

    // Höhe der Merkung: gleicher Maßstab wie echte Karten, Minimum 1 h.
    ghostStyle(g) {
      const h = Math.min(16, Math.max(1, Math.ceil(g.hours || 1)))
      return { minHeight: (28 + (h - 1) * this.hourPx) + 'px' }
    },

    ghostTitle(g) {
      return `${g.order.order_number} · ${g.order.customer_name || ''} — Überläufer vom ` +
             `${this.shortDate(g.fromIso)} · ${this.fmtHM(g.hours)} Restzeit. ` +
             `Verschwindet automatisch, sobald der Auftrag fertig gemeldet ist.`
    },

    statusClass(s) {
      return `status-${(s || 'default').replace(/_/g, '-')}`
    },

    displayMachine(m) { return displayMachineName(m) },

    // Anlieferungs-Termin beim Kunden (Chef 09.07./29.07.) — kurz, ohne Jahr,
    // damit die Zelle schmal bleibt.
    shortDate(iso) {
      const d = parseDmy(iso)
      if (!d) return ''
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`
    },

    truncate(s, n) {
      if (!s) return ''
      return s.length > n ? s.slice(0, n) + '…' : s
    },

    formatRun(v) {
      if (v === null || v === undefined || v === '') return ''
      const n = Number(String(v).replace(/[^\d]/g, ''))
      if (!isFinite(n) || n === 0) return v
      return new Intl.NumberFormat('de-DE').format(n) + ' St.'
    },

    // Mitarbeiter-Kürzel (Initialen) für die Karte — Kundenwunsch: statt Foto
    // die Initialen anzeigen, z.B. "Andreas Koch" → "AK".
    initials(name) {
      if (!name) return '?'
      const parts = String(name).trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },

    // #14 — visual indication of calculated work time. The board is a day-grid,
    // not a time axis, so we render a mini-bar inside the card whose width is
    // the geplant_start→geplant_ende duration relative to an 8h shift.
    calcHours(o) {
      if (!o.geplant_start || !o.geplant_ende) return 0
      const start = new Date(o.geplant_start).getTime()
      const end = new Date(o.geplant_ende).getTime()
      if (!isFinite(start) || !isFinite(end) || end <= start) return 0
      return (end - start) / 3_600_000
    },

    // #3 Höheneinheiten (Chef 08.–10.06.): Kartenhöhe proportional zur
    // kalkulierten Druckzeit. ½h → 1h aufgerundet (Minimal-Einheit), 8h = 1
    // volle Schicht. So sieht man auf einen Blick, wie voll eine Maschine ist.
    //   - Einheit = 1h ≈ 26px; bei 8h ergibt das ~208px (= Schichthöhe).
    //   - Inhalts-Mindesthöhe (58px) bleibt erhalten, damit kleine Jobs lesbar
    //     bleiben; über 16h (2 Schichten) wird gedeckelt (Rest = Überläufer).
    druckCardHours(o) {
      const h = this.calcHours(o)
      // Ohne kalkulierte Zeit: 1h-Basis annehmen (kleinstes Feld).
      return Math.min(16, Math.max(1, Math.ceil(h || 1)))
    },
    druckCardStyle(o) {
      const px = 58 + (this.druckCardHours(o) - 1) * this.hourPx
      return { minHeight: px + 'px' }
    },

    // Anteil der kalkulierten Druckzeit an einer 8-h-Schicht (0–100 %) — füllt
    // den seitlichen 8-h-Balken der Druck-Job-Card. 8 h = voll.
    calcBarPct(o) {
      const h = this.calcHours(o)
      if (!h) return 0
      return Math.min(100, Math.round((h / 8) * 100))
    },

    // ── Status 3: laufende Druckzeit (Besprechung 08.06.2026) ───────────────
    // Verbrauchte Zeit seit dem tatsächlichen Druckstart (läuft live über `now`,
    // im Minutentakt aktualisiert) bzw. bis zum erfassten Druckende.
    consumedHours(o) {
      if (!o || !o.tatsaechlich_start) return 0
      const start = new Date(o.tatsaechlich_start).getTime()
      if (!isFinite(start)) return 0
      const end = o.tatsaechlich_ende ? new Date(o.tatsaechlich_ende).getTime() : this.now
      return Math.max(0, (end - start) / 3_600_000)
    },
    // Druck läuft = gestartet und noch kein Ende erfasst.
    printRunning(o) {
      return this.isStarted(o) && !(o && o.tatsaechlich_ende)
    },
    // Füllgrad des hellgrünen Hintergrunds: verbraucht / kalkuliert (0–100 %).
    printProgressPct(o) {
      return progressPct(this.consumedHours(o), this.calcHours(o))
    },
    isOvertime(o) {
      return isOvertimeUtil(this.consumedHours(o), this.calcHours(o))
    },
    // M-4 — verbrauchte Zeit > 120 % der kalkulierten → rot-fette Warnung.
    isOver120(o) {
      return isOver120Util(this.consumedHours(o), this.calcHours(o))
    },
    overtimeHours(o) {
      return overtimeHoursUtil(this.consumedHours(o), this.calcHours(o))
    },
    printTimeTitle(o) {
      const base = `Kalkuliert ${this.fmtHM(this.calcHours(o))} · verbraucht ${this.fmtHM(this.consumedHours(o))}`
      return this.isOvertime(o) ? `${base} · Überzeit +${this.fmtHM(this.overtimeHours(o))}` : base
    },

    // Wie viele Stunden hat der Starttag für diesen Auftrag noch frei?
    // 'day' (Standard): ab dem geplanten Start bis Ende der letzten Schicht —
    // Start 06:00 im 2-Schicht-Betrieb = 16 h, Start 14:00 = 8 h.
    // 'shift': pauschal eine Schicht (alte Lesart).
    firstDayRoom(o, a = null) {
      if (this.overflowThreshold === 'shift') return SHIFT_HOURS
      const start = (a && a.geplant_start) || o.geplant_start
      return hoursLeftInDay(start, this.shiftSystem)
    },

    // ── Überläufer: Stunden, die nicht mehr in den Starttag passen ─────────
    // Dieselbe Regel wie die Merkungen am Folgetag (overflowIndex), damit
    // Badge und Merkung sich nie widersprechen.
    overflowHours(o) {
      const k = this.calcHours(o)
      const room = this.firstDayRoom(o)
      return k > room ? k - room : 0
    },

    // Stunden → "H:MM" (kompakt für die Karten) — geteilte Util.
    fmtHM(h) {
      return fmtHMUtil(h)
    },

    calcBarTitle(o) {
      const h = this.calcHours(o)
      if (!h) return ''
      const hh = Math.floor(h)
      const mm = Math.round((h - hh) * 60)
      return `Kalkulierte Zeit: ${hh}:${String(mm).padStart(2, '0')} h`
    },

    isPast(dateStr) {
      const d = parseDmy(dateStr)
      if (!d) return false
      const today = new Date(); today.setHours(0, 0, 0, 0)
      return d.getTime() < today.getTime()
    },

    // #19 — bereits begonnene Aufträge werden hellgrün hinterlegt.
    // "Begonnen" = es liegt eine tatsächliche Startzeit vor (an der Maschine
    // erfasst). Reine Status-Sprünge zählen NICHT (Klärung Kunde 2026-06-01).
    isStarted(o) {
      return !!(o && o.tatsaechlich_start)
    },

    // #16 — heute produzierte Stückzahl aus dem Schicht-Protokoll
    // (`produced_today`, summiert in orders#index), kleine Zahl unter dem Job.
    producedQty(o) {
      const n = Number(o && o.produced_today)
      return isFinite(n) && n > 0 ? n : 0
    },
    qtyLabel(o) {
      return new Intl.NumberFormat('de-DE').format(this.producedQty(o))
    },

    // #4 — "überfällig seit"-Datum für die graue Überfällig-Zeile.
    overdueWhen(o, kind) {
      const ref = kind === 'wv'
        ? (parseDmy(o.processing_date) || parseDmy(o.print_date) || parseDmy(o.delivery_date))
        : (parseDmy(o.print_date) || parseDmy(o.delivery_date))
      if (!ref) return ''
      return ref.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
    },

    // #17 — Erstplanung. A freshly imported order is placed as `draft`: the
    // dates come straight from Prinance and nobody has checked them, so the card
    // stays grey until a Sachbearbeiter confirms it in der Auftragstasche.
    // Orders from before the column existed have no value and count as confirmed.
    isDraft(o) {
      return o && o.planning_state === 'draft'
    },

    // "Fertig" — work is done. Sinks to the bottom of its cell and is greyed.
    isReady(o) {
      return ['delivered', 'invoice_issued', 'archived'].includes(o && o.status)
    },

    // "Vergessen" — the scheduled day has passed and the order is not done yet.
    // `iso` is the cell's day (= the order's reference day), so a card is
    // forgotten once that day is before today. Floats to the top, pink.
    isForgotten(o, iso) {
      if (this.isReady(o)) return false
      const day = parseDmy(iso)
      if (!day) return false
      const today = new Date(); today.setHours(0, 0, 0, 0)
      return day.getTime() < today.getTime()
    },

    openOrder(id) {
      this.$router.push(`/auftraege/${id}`)
    },

    // ─── Drag-to-reschedule (owner/manager) ────────────────────────────
    onDragStart(order, kind, code, ev) {
      if (!this.canMove) {
        ev.preventDefault(); return
      }
      this.dragOrder = order
      this.dragKind  = kind
      this.dragCode  = code   // source lane — needed to find the right #8 assignment
      try {
        ev.dataTransfer.effectAllowed = 'move'
        ev.dataTransfer.setData('text/plain', String(order.id))
      } catch (_) { /* some browsers are picky about dataTransfer */ }
    },

    onDragEnd() {
      this.dragOrder = null
      this.dragKind = null
      this.dragCode = null
      this.clearDragHover()
    },

    onDragOver(kind, code, iso) {
      // Only highlight cells in the same panel as the dragged card.
      if (!this.dragOrder || this.dragKind !== kind) return
      const key = `${kind}|${code}|${iso}`
      this.dragOverKey  = key
      this.dragOverCode = code  // → highlights the whole machine row
      this.dragOverIso  = iso   // → highlights the whole date column
    },

    onDragLeave() {
      this.clearDragHover()
    },

    // Reset the cell/row/column hover markers (no drop, just left the grid).
    clearDragHover() {
      this.dragOverKey  = null
      this.dragOverCode = null
      this.dragOverIso  = null
    },

    onDrop(kind, code, iso) {
      const o = this.dragOrder
      const src = this.dragCode
      const sameKind = this.dragKind === kind
      this.clearDragHover()
      this.dragOrder = null
      this.dragKind = null
      this.dragCode = null
      // Disallow cross-panel drops (a Druck job can't become a WV job here).
      if (!o || !sameKind) return
      this.moveOrder(o, kind, iso, code, src)
    },

    // Arrow buttons: nudge ±1 calendar day (no weekend snapping — lands exactly).
    // `code` = the lane the card sits on (source == target for an arrow move).
    nudgeDay(order, kind, delta, code) {
      if (!this.canMove) return
      const ref = kind === 'wv'
        ? (parseDmy(order.processing_date) || parseDmy(order.print_date) || parseDmy(order.delivery_date))
        : (parseDmy(order.print_date) || parseDmy(order.delivery_date))
      const base = ref ? new Date(ref.getTime()) : new Date()
      base.setHours(0, 0, 0, 0)
      base.setDate(base.getDate() + delta)
      this.moveOrder(order, kind, isoDay(base), code, code)
    },

    // Persist a reschedule and optimistically update the local board.
    //   kind        : 'druck' writes Drucktermin, 'wv' writes Weiterverarbeitung
    //   iso         : target day "YYYY-MM-DD"
    //   targetCode  : target lane code, or null to keep the current machine
    //   sourceCode  : lane the card came from (to find the right #8 assignment)
    //
    // WICHTIG (#8): Das Board platziert jede Karte nach dem geplant_start ihrer
    // Maschinen-Zuordnung (mit Fallback auf print_/processing_date). Wenn wir nur
    // print_date schreiben, aber die Zuordnung ein geplant_start trägt, bewegt
    // sich die Karte nicht. Darum hier auch die passende Zuordnung umplanen.
    async moveOrder(order, kind, iso, targetCode, sourceCode = null) {
      const d = parseDmy(iso)
      if (!d) return
      const y = d.getFullYear(), mo = d.getMonth() + 1, day = d.getDate()

      const payload = {}
      if (kind === 'wv') {
        payload.processing_year = y; payload.processing_month = mo; payload.processing_day = day
      } else {
        payload.print_year = y; payload.print_month = mo; payload.print_day = day
      }
      const currentCode = this.canonicalMachine(order.maschine_name).code
      const machineChanged = targetCode && targetCode !== currentCode
      if (machineChanged) payload.maschine_name = targetCode

      // The board places each card by its machine assignment (a.geplant_start,
      // else a.maschine_name + o.print_date). That assignment can be a REAL row
      // (has id) OR one the API synthesises from the legacy order columns
      // (id === null, see Order#machine_assignments_for_api). We must move THE
      // SAME object the board reads — match by lane code, id or not. Requiring an
      // id was the bug: synthesised assignments never matched → card never moved.
      const src = sourceCode || currentCode
      const assignment = Array.isArray(order.machine_assignments)
        ? order.machine_assignments.find(a => this.canonicalMachine(a.maschine_name).code === src)
        : null

      // New planned start/end: keep the original time-of-day + duration, move only
      // the calendar day. Anchor on the assignment, else the order's legacy field.
      const anchorStart = (assignment && assignment.geplant_start) || order.geplant_start
      const anchorEnde  = (assignment && assignment.geplant_ende)  || order.geplant_ende
      const anchor = anchorStart ? new Date(anchorStart) : new Date(y, mo - 1, day)
      const start  = new Date(y, mo - 1, day, anchor.getHours(), anchor.getMinutes(), 0)
      const newStart = toDateTimeStr(start)
      const newEnde  = (anchorStart && anchorEnde)
        ? toDateTimeStr(new Date(start.getTime() + (new Date(anchorEnde).getTime() - new Date(anchorStart).getTime())))
        : (anchorEnde || null)

      // Snapshot for rollback on failure.
      const snapshot = {
        print_date: order.print_date, processing_date: order.processing_date,
        print_year: order.print_year, print_month: order.print_month, print_day: order.print_day,
        processing_year: order.processing_year, processing_month: order.processing_month, processing_day: order.processing_day,
        maschine_name: order.maschine_name,
        geplant_start: order.geplant_start, geplant_ende: order.geplant_ende
      }
      const asnSnapshot = assignment
        ? { geplant_start: assignment.geplant_start, geplant_ende: assignment.geplant_ende, maschine_name: assignment.maschine_name }
        : null

      // Optimistic local update.
      if (kind === 'wv') {
        order.processing_date = iso
        order.processing_year = y; order.processing_month = mo; order.processing_day = day
      } else {
        order.print_date = iso
        order.print_year = y; order.print_month = mo; order.print_day = day
      }
      order.geplant_start = newStart
      order.geplant_ende  = newEnde
      if (machineChanged) order.maschine_name = targetCode
      if (assignment) {
        assignment.geplant_start = newStart
        assignment.geplant_ende  = newEnde
        if (machineChanged) assignment.maschine_name = targetCode
      }

      // Persist. Legacy columns always (they drive the synthesised assignment on
      // reload); nested attrs only for a REAL row (id present) — an id-less nested
      // record would create a duplicate assignment.
      payload.geplant_start = newStart
      payload.geplant_ende  = newEnde
      if (assignment && assignment.id) {
        payload.machine_assignments_attributes = [{
          id: assignment.id,
          geplant_start: newStart,
          geplant_ende: newEnde,
          ...(machineChanged ? { maschine_name: targetCode } : {})
        }]
      }

      try {
        await this.fetchData({ endpoint: `orders/${order.id}`, method: 'PUT', params: { order: payload } })
        this.$toast?.success(`${order.order_number} verschoben`)
      } catch (e) {
        console.error('Verschieben fehlgeschlagen:', e)
        Object.assign(order, snapshot)
        if (assignment && asnSnapshot) Object.assign(assignment, asnSnapshot)
        this.$toast?.error('Verschieben fehlgeschlagen')
      }
    },

    // ─── Resizable panels (persisted) ──────────────────────────────────
    clampFr(val, total, min) {
      const max = total - min
      return Math.max(min, Math.min(max, val))
    },

    beginResize(dir, ev) {
      this.resize = dir
      this._startX = ev.clientX
      this._startY = ev.clientY
      this._startTopFr  = this.topFr
      this._startLeftFr = this.leftFr
      const rect = this.$refs.rootEl.getBoundingClientRect()
      this._containerH = rect.height
      this._containerW = rect.width
      document.body.style.cursor = dir === 'h' ? 'row-resize' : 'col-resize'
      document.body.style.userSelect = 'none'
    },

    onResizeMove(ev) {
      if (!this.resize) return
      if (this.resize === 'h') {
        const dy = ev.clientY - this._startY
        const dFr = (dy / Math.max(this._containerH, 200)) * 2     // total = 2fr
        this.topFr = this.clampFr(this._startTopFr + dFr, 2, 0.3)
      } else {
        const dx = ev.clientX - this._startX
        const dFr = (dx / Math.max(this._containerW, 200)) * 2.4   // total = 2.4fr
        this.leftFr = this.clampFr(this._startLeftFr + dFr, 2.4, 0.3)
      }
    },

    endResize() {
      if (!this.resize) return
      this.resize = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      try {
        localStorage.setItem('kapplan_tri_layout', JSON.stringify({
          topFr:  +this.topFr.toFixed(3),
          leftFr: +this.leftFr.toFixed(3)
        }))
      } catch { /* ignore quota errors */ }
    },

    restoreLayout() {
      try {
        const raw = localStorage.getItem('kapplan_tri_layout')
        if (!raw) return
        const { topFr, leftFr } = JSON.parse(raw)
        if (Number.isFinite(topFr))  this.topFr  = this.clampFr(topFr, 2, 0.3)
        if (Number.isFinite(leftFr)) this.leftFr = this.clampFr(leftFr, 2.4, 0.3)
      } catch { /* ignore parse errors */ }
    }
  }
}
</script>

<style scoped lang="scss" src="@/styles/kapazitaetplannung.scss"></style>
